import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLiveMarket } from "@/lib/useLiveMarket";
import PopularBadge from "@/components/common/PopularBadge";
import Reveal from "../Reveal";

const LIVE_IDS = ["bitcoin", "ethereum", "solana", "ripple"];

function fmtPrice(n) {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1000) return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
  if (n >= 1) return n.toLocaleString("en-US", { maximumFractionDigits: 3 });
  return n.toLocaleString("en-US", { maximumFractionDigits: 5 });
}

function fmtPct(pct) {
  if (!Number.isFinite(pct)) return "—";
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

function MockChart({ testId, changePct }) {
  const up = Number(changePct) >= 0;
  return (
    <div
      className="w-full max-w-[560px] rounded-2xl border border-[var(--cm-border)] bg-white p-6 shadow-[0_24px_48px_-28px_rgba(29,45,63,0.2)] cm-hover-lift"
      data-testid={testId}
    >
      <div className="flex justify-between text-xs text-cm-grey mb-4">
        <span className="font-bold text-cm-navy">BTC/USDT</span>
        <span className={`font-semibold ${up ? "text-emerald-600" : "text-red-500"}`}>
          {fmtPct(changePct)}
        </span>
      </div>
      <div className="h-[200px] flex items-end gap-1">
        {[40, 55, 45, 70, 60, 85, 75, 90, 65, 80, 95, 72].map((h, i) => (
          <div
            key={i}
            className={`flex-1 rounded-t ${up ? "bg-gradient-to-t from-cm-blue/25 to-cm-blue" : "bg-gradient-to-t from-red-200 to-red-400"}`}
            style={{ height: `${h}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function MockCandles({ testId }) {
  return (
    <div
      className="w-full max-w-[620px] rounded-2xl border border-[var(--cm-border)] bg-white p-6 shadow-[0_24px_48px_-28px_rgba(29,45,63,0.2)] cm-hover-lift"
      data-testid={testId}
    >
      <div className="grid grid-cols-8 gap-2 h-[180px] items-end">
        {Array.from({ length: 16 }).map((_, i) => (
          <div key={i} className="flex flex-col items-center justify-end gap-0.5 h-full">
            <div
              className={`w-1 rounded-sm ${i % 3 === 0 ? "bg-red-400/80" : "bg-emerald-500/85"}`}
              style={{ height: `${30 + (i * 7) % 60}%` }}
            />
            <div className="w-2 h-1 rounded-full bg-cm-navy/15" />
          </div>
        ))}
      </div>
    </div>
  );
}

function MockTable({ testId, live }) {
  const rows = [
    { sym: "BTC", id: "bitcoin" },
    { sym: "ETH", id: "ethereum" },
    { sym: "SOL", id: "solana" },
    { sym: "XRP", id: "ripple" },
  ];
  return (
    <div
      className="w-full max-w-[680px] rounded-2xl border border-[var(--cm-border)] bg-white overflow-hidden shadow-[0_24px_48px_-28px_rgba(29,45,63,0.2)] cm-hover-lift"
      data-testid={testId}
    >
      <div className="grid grid-cols-4 gap-2 px-4 py-3 bg-cm-lavender/50 text-xs font-bold text-cm-navy">
        <span>Pair</span>
        <span>Price</span>
        <span>24h</span>
        <span>Status</span>
      </div>
      {rows.map(({ sym, id }) => {
        const t = live[id];
        const up = t?.up ?? true;
        return (
          <div
            key={sym}
            className="grid grid-cols-4 gap-2 px-4 py-3 border-t border-[var(--cm-border)]/60 text-sm"
          >
            <span className="font-semibold text-cm-navy">{sym}/USDT</span>
            <span className="text-cm-navy tabular-nums font-medium">{fmtPrice(t?.price)}</span>
            <span className={`font-semibold tabular-nums ${up ? "text-emerald-600" : "text-red-500"}`}>
              {fmtPct(t?.changePct)}
            </span>
            <span className="text-[11px] font-bold text-cm-blue uppercase tracking-wide">Live</span>
          </div>
        );
      })}
    </div>
  );
}

function FeatureBlock({ tag, title, body, cta, ctaTo, visual, reverse, popular }) {
  return (
    <div
      className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${reverse ? "" : ""}`}
    >
      <Reveal variant="fade-right" className={`max-w-[520px] ${reverse ? "lg:order-2" : ""}`}>
        <div className="flex items-center gap-2 mb-3">
          {popular && <PopularBadge testId="endless-otc-popular-badge" />}
          <span className="cm-landing-section-label inline-block">{tag}</span>
        </div>
        <h3 className="text-[26px] lg:text-[30px] font-bold text-cm-navy leading-[1.2]">{title}</h3>
        <p className="mt-5 text-cm-grey text-[15px] leading-[1.85]">{body}</p>
        <Link
          to={ctaTo}
          className="mt-6 inline-flex items-center gap-2 text-[14px] font-bold text-cm-blue hover:gap-3 transition-all"
        >
          {cta}
          <ArrowRight size={16} />
        </Link>
      </Reveal>
      <Reveal
        variant="fade-left"
        delay={120}
        className={`flex justify-center ${reverse ? "lg:justify-start lg:order-1" : "lg:justify-end"}`}
      >
        {visual}
      </Reveal>
    </div>
  );
}

export default function EndlessPossibilities() {
  const live = useLiveMarket(LIVE_IDS);
  const btcPct = live.bitcoin?.changePct ?? 0;

  return (
    <section
      className="relative overflow-hidden cm-landing-section-alt"
      data-testid="endless-possibilities"
    >
      <div className="cm-container relative">
        <Reveal variant="fade-up" className="text-center mb-16 lg:mb-20">
          <div className="cm-landing-section-label mb-4">Deep dive</div>
          <h2 className="cm-landing-section-title">Built for how you actually trade</h2>
          <p className="mt-4 text-[15px] text-cm-grey max-w-[560px] mx-auto leading-[1.75]">
            From your first INR deposit to size tickets on the OTC desk — every workflow stays on
            one secure platform.
          </p>
        </Reveal>

        <div className="space-y-16 lg:space-y-20">
          <FeatureBlock
            tag="Spot"
            title="Buy crypto, the smart way"
            body="Instant access to major pairs with transparent fees, INR-friendly funding, and professional charts. Start with BTC, ETH, or USDT in minutes."
            cta="Trade spot"
            ctaTo="/trade/spot/BTC-USDT"
            visual={<MockChart testId="bitcoin-card" changePct={btcPct} />}
          />
          <FeatureBlock
            tag="Wallets"
            title="Sell anytime, withdraw with control"
            body="Fast settlements, multiple withdrawal paths, and address whitelisting so you always know where funds are headed."
            cta="Open wallets"
            ctaTo="/wallets"
            visual={<MockCandles testId="candlestick-card" />}
            reverse
          />
          <FeatureBlock
            tag="OTC"
            popular
            title="Large trades, locked quotes"
            body="Request a quote from our desk for block-size tickets — transparent fees, same-day INR settlement, and dedicated support for treasuries and institutions."
            cta="Explore OTC desk"
            ctaTo="/otc"
            visual={<MockTable testId="token-card" live={live} />}
          />
        </div>
      </div>
    </section>
  );
}
